// Lightweight, dependency-free state/types for the Tier-2 semantic intent fallback (round-24,
// 2026-08-31 — DESIGN.md §17, semanticIntent.ts). Split out of semanticIntent.ts on purpose: this
// file has NO import of `@xenova/transformers` (which drags in onnxruntime-web, ~300kB+ gzipped
// of JS alone, before even fetching the ~163MB of model/WASM binaries) so it's safe to import
// STATICALLY from anywhere — DebugStt.vue reads `semanticDebug`/`MIN_SIMILARITY`/`MIN_MARGIN`
// from here without pulling the heavy embedding library into the app's main JS bundle. Only
// stt.ts's actual classification call sites `await import('./semanticIntent')` dynamically, so
// that code (and its dependencies) is fetched/parsed only once the model is actually needed —
// see stt.ts's SEMANTIC_MODEL_PRELOAD_DELAY_MS and semanticIntent.ts's module docblock.
import { reactive } from 'vue'
import type { SemanticIntentId } from './semanticExemplars'
import type { SttCommandEvent } from './stt'

export type SemanticModelState = 'idle' | 'loading' | 'ready' | 'error'

export interface SemanticIntentScore {
  intent: SemanticIntentId
  score: number
}

export interface SemanticClassification {
  transcript: string
  /** Every intent's best-exemplar score, ranked best first. */
  ranked: SemanticIntentScore[]
  top: SemanticIntentScore
  runnerUp: SemanticIntentScore | null
  margin: number
  /** Whether top clears MIN_SIMILARITY AND MIN_MARGIN over runnerUp. */
  accepted: boolean
  /** Populated when accepted — what stt.ts should dispatch as an SttCommandEvent. */
  command?: { command: SttCommandEvent['command']; color?: 'red' | 'blue'; answer?: 'yes' | 'no' }
  /** Populated when NOT accepted — why (for tracking, DESIGN.md §16). */
  rejectReason?: 'below_min_similarity' | 'below_margin'
  latencyMs: number
}

interface SemanticDebugState {
  modelState: SemanticModelState
  modelError: string | null
  modelLoadMs: number | null
  lastClassification: SemanticClassification | null
}

/** Read by DebugStt.vue (model load state, last classification, latency) — updated by
 *  semanticIntent.ts once it's actually loaded (dynamic import, see that module's docblock). */
export const semanticDebug = reactive<SemanticDebugState>({
  modelState: 'idle',
  modelError: null,
  modelLoadMs: null,
  lastClassification: null,
})

// --------------------------------------------------------------------------------------------
// Acceptance thresholds — the tuning surface for HOW SURE the model must be before a semantic
// match is acted on. Named, documented constants, same convention as voiceScore.ts's
// CONFIDENCE_* constants. Deliberately conservative — the standing rule (win_claim/point_claim's
// AGGRESSIVE_* gates, CLAUDE.md) is false negatives over unreasonable false positives: an
// unrecognized utterance doing nothing is fine, a misfired navigation jump or corner claim is
// not. Kept here (not in semanticIntent.ts) so DebugStt.vue can display them without a dynamic
// import just to read two numbers.
// --------------------------------------------------------------------------------------------

/** Minimum cosine similarity (embeddings are L2-normalized, so this IS the raw cosine) between
 *  the utterance and its best-matching exemplar for that exemplar's intent to even be considered
 *  the top intent. Tuned empirically against scripts/semantic-sanity.mjs's real-phrasing table —
 *  see that script's header for the measured score distribution ("clean match" utterances
 *  clustered ~0.83-0.997, unrelated chatter topped out ~0.28-0.66 against this exemplar set). */
export const MIN_SIMILARITY = 0.6

/** Minimum gap the top-scoring intent must hold over the runner-up (best score among all OTHER
 *  intents) to be acted on. A close top-two (e.g. "start" vs "nav_new_match" both scoring high)
 *  means the utterance is genuinely ambiguous between two different actions — per the
 *  false-negative-over-false-positive rule, that ambiguity means do nothing, not "pick the
 *  higher one anyway." */
export const MIN_MARGIN = 0.08
